// Shared Quill setup for every editor that writes submission body HTML.
//
// Two places edit that HTML: the franchisee's submission form (ContentTab) and
// marketing's approval drawer (ContentApprovalsPage). They MUST use the same
// modules, formats and blots — the marketer is editing markup the franchisee's
// editor produced, and an editor that doesn't know the embed blot would strip
// every embed out of the post on save.
//
// Importing this module registers the blots as a side effect, so any editor
// that uses these configs is guaranteed to understand what it's parsing.

import { showToast } from '../components/Toast';
import { detectEmbedProvider, normalizeEmbedUrl, EMBED_PROVIDER_LABELS } from './embeds';
import { registerEmbedBlot, EMBED_BLOT_NAME } from './quillEmbedBlot';
import { registerNoH1Header } from './quillHeadings';

// Register the rich-media embed blot once, before any editor mounts.
registerEmbedBlot();
// Clamp the header format to H2/H3 so a pasted <h1> can't become a second H1
// on the published page (the title is the only H1).
registerNoH1Header();

export { EMBED_BLOT_NAME };

export const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

export const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link'];

// Toolbar handler for the blog "Embed" button: prompt for a URL, validate it
// against the provider allowlist, and insert a block-embed blot.
export function amadaEmbedHandler(this: { quill: any }) {
  const raw = window.prompt(
    `Paste a link to embed (${EMBED_PROVIDER_LABELS}):`,
  );
  if (raw == null) return; // cancelled
  const url = raw.trim();
  if (!url) return;
  const def = detectEmbedProvider(url);
  if (!def) {
    showToast(`That link isn't a supported embed. Supported: ${EMBED_PROVIDER_LABELS}.`, {
      variant: 'error',
    });
    return;
  }
  const quill = this.quill;
  const range = quill.getSelection(true);
  const index = range ? range.index : quill.getLength();
  quill.insertEmbed(index, EMBED_BLOT_NAME, { provider: def.slug, url: normalizeEmbedUrl(url) }, 'user');
  quill.setSelection(index + 1, 0, 'silent');
}

// Blog editor gets the embed button + blot on top of the shared toolbar.
export const QUILL_MODULES_BLOG = {
  toolbar: {
    container: [
      [{ header: [2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', EMBED_BLOT_NAME],
      ['clean'],
    ],
    handlers: { [EMBED_BLOT_NAME]: amadaEmbedHandler },
  },
};

export const QUILL_FORMATS_BLOG = [...QUILL_FORMATS, EMBED_BLOT_NAME];

/**
 * True when Quill's HTML holds no visible content. Quill never yields an empty
 * string — an emptied editor serializes to `<p><br></p>`.
 */
export function isQuillBlank(html: string): boolean {
  if (!html) return true;
  if (/<(img|iframe)\b/i.test(html)) return false;
  if (html.includes('data-embed-url')) return false;
  return (
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
      .replace(/\u00a0/g, ' ')
      .trim().length === 0
  );
}
